import React, { useCallback, useEffect, useState } from "react";
import { FlaskConical, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ConfirmModal from "@/components/ConfirmModal";
import { PaResultBody } from "@/components/pa/PaResultBody";
import { PaDossierPanel } from "@/components/pa/PaDossierPanel";

const base = (caseId) => `/v2/pa/cases/${encodeURIComponent(caseId)}/analyses`;
const errMsg = (e) => e?.response?.data?.detail?.message || "Operazione non riuscita";

const ScenarioPicker = ({ scenarios, busy, onAnalyze, picked, onPick }) => (
  <div className="space-y-2">
    {scenarios.map((s) => (
      <div key={s.scenario_key} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl p-3" data-testid={`pa-scenario-${s.scenario_key}`}>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={picked.includes(s.scenario_key)} disabled={!s.applicable}
            onChange={() => onPick(s.scenario_key)} data-testid={`pa-compare-pick-${s.scenario_key}`} />
          <span className="font-semibold">{s.title}</span>
          {s.status === "da_analizzare" && <span className="text-[10px] uppercase font-bold text-gray-400">da analizzare</span>}
        </label>
        {s.analyzed_version ? (
          <span className="text-xs font-semibold text-gray-500" data-testid={`pa-analyzed-${s.scenario_key}`}>Già approfondita · v{s.analyzed_version}</span>
        ) : (
          <button disabled={busy || !s.applicable} onClick={() => onAnalyze(s.scenario_key)}
            className="vg-btn-secondary px-3 py-1.5 text-xs" data-testid={`pa-analyze-${s.scenario_key}`}>Approfondisci</button>
        )}
      </div>
    ))}
  </div>
);

const ResultItem = ({ item, onShare, selected, onSelect }) => (
  <div className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid={`pa-analysis-${item.analysis_id}`}>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1" checked={selected} onChange={() => onSelect(item.analysis_id)}
          aria-label="Includi nel dossier" data-testid={`pa-dossier-pick-${item.analysis_id}`} />
        <span>
          <span className="block font-semibold text-[#0A0A0A]">{item.title}</span>
          <span className="block text-xs text-gray-400">{item.author_label} · versione {item.version} · {new Date(item.created_at).toLocaleString("it-IT")} · base DECIDI v{item.base_decision_version}</span>
        </span>
      </label>
      {item.shared ? (
        <span className="flex items-center gap-1 text-xs font-bold text-green-700" data-testid={`pa-analysis-shared-${item.analysis_id}`}><Check size={14} /> Condivisa</span>
      ) : (
        <button onClick={() => onShare(item)} className="vg-btn-primary flex items-center gap-1 px-3 py-1.5 text-xs" data-testid={`pa-analysis-share-${item.analysis_id}`}>
          <Share2 size={13} /> Condividi con il proprietario
        </button>
      )}
    </div>
    <PaResultBody item={item} />
  </div>
);

export default function PaAnalysesSection({ caseId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState([]);
  const [toShare, setToShare] = useState(null);
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const load = useCallback(() => {
    api.get(base(caseId)).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e)));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const run = async (path, body, msg) => {
    setBusy(true);
    try { await api.post(`${base(caseId)}/${path}`, body); toast.success(msg); setPicked([]); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const pick = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p.slice(-1), k]));

  return (
    <section className="rounded-2xl bg-white border border-gray-200 p-6 space-y-4" data-testid="pa-analyses-section">
      <div className="flex items-center gap-2 text-[#B40101]"><FlaskConical size={18} /><h2 className="text-lg font-bold text-[#0A0A0A]">Elaborazioni professionali</h2></div>
      <p className="text-xs text-gray-500">Analisi V3 eseguite da te sul caso. Restano riservate finché non scegli «Condividi con il proprietario» e non modificano il DECIDI del proprietario.</p>
      {error && <p className="text-sm text-gray-500" data-testid="pa-analyses-error">{error}</p>}
      {data && (
        <>
          <p className="text-xs uppercase tracking-wider font-bold text-gray-500" data-testid="pa-original-strategies-title">
            Strategie originali del proprietario · DECIDI v{data.base_decision_version}
          </p>
          <ScenarioPicker scenarios={data.scenarios} busy={busy} picked={picked} onPick={pick}
            onAnalyze={(k) => run("scenario", { scenario_key: k }, "Elaborazione salvata")} />
          <button disabled={busy || picked.length !== 2} onClick={() => run("compare", { scenario_keys: picked }, "Confronto salvato")}
            className="vg-btn-secondary px-4 py-2 text-sm" data-testid="pa-compare-btn">Confronta le 2 strategie selezionate</button>
          {busy && <p className="text-xs text-gray-500" data-testid="pa-analyses-busy">Elaborazione in corso, può richiedere fino a un minuto…</p>}
          <p className="text-xs uppercase tracking-wider font-bold text-gray-500 pt-2" data-testid="pa-own-analyses-title">
            Le tue elaborazioni professionali ({data.items.length})
          </p>
          <div className="space-y-3" data-testid="pa-analyses-list">
            {data.items.map((i) => <ResultItem key={i.analysis_id} item={i} onShare={setToShare}
              selected={sel.includes(i.analysis_id)} onSelect={toggleSel} />)}
          </div>
          {data.items.length > 0 && <PaDossierPanel caseId={caseId} items={data.items} selected={sel} />}
        </>
      )}
      <ConfirmModal open={!!toShare} testid="confirm-pa-share" title="Condividere con il proprietario?"
        message="Il proprietario vedrà questa elaborazione nella sua Scrivania, con il tuo nome, la data e la versione. Gli appunti interni non vengono condivisi."
        onCancel={() => setToShare(null)}
        onConfirm={() => { const it = toShare; setToShare(null); run(`${it.analysis_id}/share`, {}, "Condivisa con il proprietario"); }} />
    </section>
  );
}
